// Stav aplikace (state), výběr firmy, role a přístupy, společné výpočty a pomocné funkce.
// (Krok B1b: rozděleno ze src/app.js beze změny kódu; importy/exporty doplněny automaticky.)
import { DASHBOARD_CONFIG_VERSION, DASHBOARD_GROUP_SIZES, DASHBOARD_KPI_IDS, DASHBOARD_SECTIONS, DASHBOARD_SECTION_META, DASHBOARD_SIZES, DASHBOARD_V2_NEW_IDS, isDashGroupId } from './dashboard-config.js';
import { loadPageSizePref } from './pagination.js';
import { assetsCap, db } from './boot.js';
import { isCorrectiveAssignedToMe } from '../views/places.js';
import { accessName } from './access.js';
import { auditWorkplaceIds, presentParticipantIds, workplacesLabel, workplaceResponsibleId } from '../../supabase/functions/_shared/calc.js';

// ---------- current legal entity (právní subjekt) selection ----------
// Which company's data is shown is a per-viewer convenience (like theme),
// so it's stored in localStorage, not synced between viewers.
// Osobní seznamy: Přehled, Historie, Opatření a Plán ukazují ve výchozím
// stavu jen to, co se týká přihlášeného uživatele ("Moje"); přepínačem jde
// zobrazit "Moje pracoviště" nebo vše. Volba se pamatuje pro každou stránku
// zvlášť, per-prohlížeč (localStorage), jako téma. Bez vlastní volby platí
// dřívější společná ('audit_app_scope_v1': 'all' | 'mine').
var SCOPE_KEY = 'audit_app_scope_v1';
var SCOPE_PAGES = ['dashboard','history','corrective','schedule'];
var SCOPE_MODES = ['mine','workplaces','all'];
function loadScopes(){
  var legacy = 'mine', out = {};
  try{ if(localStorage.getItem(SCOPE_KEY)==='all') legacy = 'all'; }catch(e){}
  SCOPE_PAGES.forEach(function(pg){
    var v = null;
    try{ v = localStorage.getItem(SCOPE_KEY+'_'+pg); }catch(e){}
    out[pg] = SCOPE_MODES.indexOf(v)>=0 ? v : legacy;
  });
  return out;
}
function saveScope(pg, mode){ try{ localStorage.setItem(SCOPE_KEY+'_'+pg, mode); }catch(e){} }
var COMPANY_KEY = 'audit_app_company_v1';
function loadCurrentCompanyId(){
  try{ return localStorage.getItem(COMPANY_KEY) || ''; }catch(e){ return ''; }
}
function saveCurrentCompanyId(id){
  try{ localStorage.setItem(COMPANY_KEY, id||''); }catch(e){}
}

var DASHBOARD_KEY = 'audit_app_dashboard_v1';
// Rozložení dashboardu je sdílené v rámci účtu (uložené v profilu uživatele
// v DB: profiles/<myId>.dashboardSettings), aby bylo stejné na všech
// zařízeních. localStorage tu zůstává jen jako rychlá lokální mezipaměť,
// aby se při startu (než doběhne načtení profilu z DB) nenablikly výchozí
// hodnoty — see parseDashboardConfig/profiles onSnapshot below.
function parseDashboardConfig(raw){
  var defOrder = DASHBOARD_SECTIONS.slice();
  var defVisible = {}; var defLimits = {}; var defSizes = {};
  DASHBOARD_SECTIONS.forEach(function(id){
    defVisible[id]=true; defLimits[id]=DASHBOARD_SECTION_META[id].def||0; defSizes[id]=DASHBOARD_SECTION_META[id].defSize||'m';
  });
  var defFilter = {period:'all', branch:'', wp:'', type:'', group:''};
  var def = {v:DASHBOARD_CONFIG_VERSION, order:defOrder, visible:defVisible, limits:defLimits, sizes:defSizes, groups:{}, filter:defFilter};
  if(!raw || typeof raw!=='object') return def;
  var p = raw;
  var rawOrder = Array.isArray(p.order) ? p.order.slice() : [];
  var rawVisible = (p.visible && typeof p.visible==='object') ? p.visible : {};
  var visible = Object.assign({}, defVisible, rawVisible);
  var limits = Object.assign({}, defLimits, (p.limits && typeof p.limits==='object') ? p.limits : {});
  var kpisVisible = rawVisible.kpis!==false;
  if(!(p.v>=2)){
    // Rozložení z doby před novými statistikami: ukazatele a Vývoj kvality
    // nahoru, nové dlaždice za ně, dosavadní v uživatelově pořadí za nimi.
    var oldRest = rawOrder.filter(function(id){ return id!=='trend'; });
    rawOrder = ['kpis','trend'].concat(DASHBOARD_V2_NEW_IDS.filter(function(id){ return rawOrder.indexOf(id)<0; }), oldRest);
    kpisVisible = rawVisible.stats!==false;
    if(!limits.trend) limits.trend = 12;
  }
  if(!(p.v>=3)){
    // Jedna dlaždice "Hlavní ukazatele" → šest samostatných na jejím místě.
    var at = rawOrder.indexOf('kpis');
    if(at<0) at = 0; else rawOrder.splice(at, 1);
    rawOrder.splice.apply(rawOrder, [at, 0].concat(DASHBOARD_KPI_IDS));
    DASHBOARD_KPI_IDS.forEach(function(id){ visible[id] = kpisVisible; });
  }
  // Skupiny ukazatelů {gid: {title, items:[kpi…]}}; ukazatel je nejvýš v jedné
  // skupině a v pořadí (order) je místo něj id skupiny.
  var groups = {}, grouped = {};
  var rawGroups = (p.groups && typeof p.groups==='object') ? p.groups : {};
  Object.keys(rawGroups).forEach(function(gid){
    var g = rawGroups[gid];
    if(!isDashGroupId(gid) || !g || typeof g!=='object') return;
    var items = (Array.isArray(g.items) ? g.items : []).filter(function(id){
      if(DASHBOARD_KPI_IDS.indexOf(id)<0 || grouped[id]) return false;
      grouped[id] = true; return true;
    });
    groups[gid] = {title: String(g.title||'').slice(0, 60), items: items};
  });
  var known = function(id){ return DASHBOARD_SECTIONS.indexOf(id)>=0 || !!groups[id]; };
  [visible, limits].forEach(function(m){ Object.keys(m).forEach(function(k){ if(!known(k)) delete m[k]; }); });
  var order = [];
  rawOrder.forEach(function(id){ if(known(id) && !grouped[id] && order.indexOf(id)<0) order.push(id); });
  DASHBOARD_SECTIONS.concat(Object.keys(groups)).forEach(function(id){ if(!grouped[id] && order.indexOf(id)<0) order.push(id); });
  var sizes = Object.assign({}, defSizes, (p.sizes && typeof p.sizes==='object') ? p.sizes : {});
  DASHBOARD_SECTIONS.forEach(function(id){ if(DASHBOARD_SIZES.indexOf(sizes[id])<0) sizes[id] = defSizes[id]; });
  Object.keys(groups).forEach(function(gid){ if(DASHBOARD_GROUP_SIZES.indexOf(sizes[gid])<0) sizes[gid] = 'l'; if(visible[gid]==null) visible[gid] = true; });
  Object.keys(sizes).forEach(function(k){ if(!known(k)) delete sizes[k]; });
  var filter = Object.assign({}, defFilter, (p.filter && typeof p.filter==='object') ? p.filter : {});
  return {v:DASHBOARD_CONFIG_VERSION, order:order, visible:visible, limits:limits, sizes:sizes, groups:groups, filter:filter};
}
function loadDashboardConfig(){
  try{
    var raw = localStorage.getItem(DASHBOARD_KEY);
    return parseDashboardConfig(raw ? JSON.parse(raw) : null);
  }catch(e){ return parseDashboardConfig(null); }
}
var DASHBOARD_SYNC_DEBOUNCE_MS = 700;
var dashboardSyncTimer = null;
function saveDashboardConfig(c){
  try{ localStorage.setItem(DASHBOARD_KEY, JSON.stringify(c)); }catch(e){}
  if(!db || !state.myId) return;
  clearTimeout(dashboardSyncTimer);
  dashboardSyncTimer = setTimeout(function(){
    queueWrite(function(){ return db.doc('profiles/'+state.myId).update({dashboardSettings:c}); });
  }, DASHBOARD_SYNC_DEBOUNCE_MS);
}
var dashboardConfig = loadDashboardConfig();
function setDashboardConfig(c){ dashboardConfig = c; }

var ICONS = {
  camera:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h3l2-2h6l2 2h3a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z"/><circle cx="12" cy="13" r="3.5"/></svg>',
  plus:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>',
  trash:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0-1 14a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1L6 6"/></svg>',
  qr:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3v3h-3zM20 14v1M14 20h1M17 17h4v4h-4"/></svg>',
  edit:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>',
  up:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>',
  down:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12l7 7 7-7"/></svg>',
  chev:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>',
  sidebar:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M8.5 7v.01M8.5 10.5v.01M8.5 14v.01M8.5 17.5v.01" stroke-width="2.4"/></svg>',
  viewSplit:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M10 4v16M6 8h1M6 12h1M6 16h1"/></svg>',
  viewTable:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9.5h18M3 15h18M9 4v16M15 4v16"/></svg>',
  tasks:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6l1.5 1.5L7 5"/><path d="M3 12l1.5 1.5L7 11"/><path d="M3 18l1.5 1.5L7 17"/><path d="M11 6h10M11 12h10M11 18h10"/></svg>',
  grip:'<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.6"/><circle cx="15" cy="6" r="1.6"/><circle cx="9" cy="12" r="1.6"/><circle cx="15" cy="12" r="1.6"/><circle cx="9" cy="18" r="1.6"/><circle cx="15" cy="18" r="1.6"/></svg>',
  empty:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3 8-8"/><path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9"/></svg>',
  download:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12M7 10l5 5 5-5"/><path d="M4 19h16"/></svg>',
  paperclip:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>',
  fileDoc:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>',
  feedback:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M12 7v4M12 14h.01"/></svg>',
  mail:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>',
  bell:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8a6 6 0 1 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M13.7 21a2 2 0 0 1-3.4 0"/></svg>',
  copy:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1"/></svg>',
  navDashboard:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 13h5v7H3zM10 7h5v13h-5zM17 3h5v17h-5z"/></svg>',
  navAudit:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3 8-8"/><path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9"/></svg>',
  navHistory:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>',
  navQuestions:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6h16M4 12h10M4 18h7"/><circle cx="19" cy="18" r="2"/></svg>',
  navWorkplaces:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 9h1M14 9h1M9 13h1M14 13h1M9 17h1M14 17h1"/></svg>',
  navUsers:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="8" r="3.2"/><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5"/><circle cx="17.5" cy="9" r="2.4"/><path d="M15 14.3c2.2.4 3.8 2 3.8 4.7"/></svg>',
  navSettings:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.09a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.09a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>',
  profile:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>',
  sun:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/></svg>',
  moon:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z"/></svg>',
  palette:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a9 9 0 1 0 0 18c1.1 0 2-.9 2-2 0-.5-.2-1-.5-1.3-.3-.4-.5-.8-.5-1.3 0-1.1.9-2 2-2h2a4 4 0 0 0 4-4c0-4.4-4-7.4-9-7.4z"/><circle cx="7.5" cy="10.5" r="1.1" fill="currentColor"/><circle cx="12" cy="7.5" r="1.1" fill="currentColor"/><circle cx="16.5" cy="10.5" r="1.1" fill="currentColor"/></svg>',
  company:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="12" rx="1.5"/><path d="M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M3 13h18"/></svg>',
  sliders:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0"/><circle cx="16" cy="6" r="2"/><circle cx="10" cy="12" r="2"/><circle cx="18" cy="18" r="2"/></svg>',
  sort:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 4v16M3 8l4-4 4 4M17 20V4M13 16l4 4 4-4"/></svg>',
  groups:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="3"/><circle cx="5" cy="10" r="2.2"/><circle cx="19" cy="10" r="2.2"/><path d="M7 20c0-3 2.2-5 5-5s5 2 5 5"/><path d="M1.5 19c0-2.2 1.5-3.8 3.5-3.8M22.5 19c0-2.2-1.5-3.8-3.5-3.8"/></svg>',
  filter:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 5h16l-6 7v6l-4 2v-8z"/></svg>',
  navCorrective:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>',
  navSchedule:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/><circle cx="8" cy="15" r="1.2" fill="currentColor" stroke="none"/><circle cx="12" cy="15" r="1.2" fill="currentColor" stroke="none"/><circle cx="16" cy="15" r="1.2" fill="currentColor" stroke="none"/></svg>',
  arrLeft:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>',
  arrRight:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>'
};

// Odkaz "Zahájit audit" z kalendářové události (viz calendar-feed Edge
// Function a App.startScheduledAudit níž) má tvar "...?startSchedule=
// <klíč termínu>". Přečte se jednou hned při startu appky; samotné
// vyřízení čeká v maybeProcessDeepLink() na to, až budou načtená potřebná
// data (plány, pracoviště, audity), ať appka nezačíná zakládat audit nad
// neúplným stavem.
var pendingDeepLinkKey = null;
try {
  var __dlParam = new URLSearchParams(window.location.search).get('startSchedule');
  if(__dlParam) pendingDeepLinkKey = decodeURIComponent(__dlParam);
} catch(e){}
var deepLinkProcessed = false;
function maybeProcessDeepLink(){
  if(deepLinkProcessed || !pendingDeepLinkKey) return;
  if(!(state.myId && state.rolesReady && state.ready.schedules && state.ready.workplaces && state.ready.audits && state.ready.auditTypes)) return;
  deepLinkProcessed = true;
  var key = pendingDeepLinkKey;
  try {
    var cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('startSchedule');
    history.replaceState(null, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
  } catch(e){}
  // Napřed na Plán auditů -- i kdyby se audit nepodařilo rovnou spustit
  // (cizí auditor, už splněno, moc brzy…), skončí uživatel na obrazovce,
  // která k tomu má kontext, místo na výchozím Přehledu. App.startScheduledAudit
  // sama ukáže konkrétní důvod formou toastu.
  state.view = 'schedule';
  setTimeout(function(){ App.startScheduledAudit(key); }, 0);
}

var state = {
  view: 'dashboard',
  workplaces: new Map(),
  branches: new Map(),
  companies: new Map(),
  currentCompanyId: loadCurrentCompanyId(),
  auditTypes: new Map(),
  groups: new Map(),
  questions: new Map(),
  subquestions: new Map(),
  audits: new Map(),
  correctiveActions: new Map(),
  schedules: new Map(),
  auditorGroups: new Map(),
  feedback: new Map(), // podněty (autor vidí své, Administrátor všechny — RLS)
  delegations: new Map(), // zástupy u termínů plánu (schedule_delegations, klíč = klíč termínu)
  newAuditorGroupName: '',
  editAuditorGroupId: null,
  editAuditorGroupName: '',
  ready: {workplaces:false, branches:false, companies:false, auditTypes:false, groups:false, questions:false, subquestions:false, audits:false, profiles:false, branding:false, correctiveActions:false, schedules:false, auditorGroups:false, feedback:false},
  scheduleCalendarY: null,
  scheduleCalendarM: null,
  scheduleCalendarSelectedDay: null,
  editScheduleId: null,
  scheduleDraftCompanyIds: [],
  scheduleDraftBranchIds: [],
  scheduleDraftWorkplaceIds: [],
  scheduleDraftAuditTypeId: '',
  scheduleDraftStartDate: '',
  scheduleDraftTime: '',
  scheduleDraftFreqValue: 3,
  scheduleDraftFreqUnit: 'months',
  scheduleDraftAuditorIds: [],
  scheduleDraftActive: true,
  scheduleDraftSpreadWorkplaces: false,
  scheduleDraftSkipWeekends: true,
  dbMissing: false,
  activeAuditId: null,
  auditTab: null,
  workplaceDetailId: null, // stránka pracoviště (QR kód)
  qrPrint: null, // dialog tisku QR štítků // Audity: 'open' | 'start' (null = podle effectiveAuditTab)
  draftAudit: null,
  collapsedGroups: new Set(),
  collapsedWpBranches: new Set(),
  collapsedWpChildren: new Set(), // nadřazená pracoviště se schovanými podřízenými
  auditParticipantsEditing: false,
  openNotes: new Set(),
  historyDetailId: null,
  historyDetailReturnTo: null,
  historyFilterWp: '',
  historyFilterType: '',
  historyFilterGroup: '',
  historyFilterResult: '',
  historyFilterAuditor: '',
  historyFilterCorrectiveStatus: '',
  historySearch: '',
  historyDateFrom: '',
  historyDateTo: '',
  historyFiltersOpen: false,
  dashboardFiltersOpen: false,
  dashTrendPick: null,
  dashAreaOpen: null,
  historySortBy: 'closedAt',
  historySortDir: 'desc',
  historySelectMode: false,
  // ---- stránkování (společné pro Historii, Opatření, Plán auditů,
  // rozpracované/naplánované audity a Uživatele a role) ----
  pageSize: loadPageSizePref(),
  historyPage: 1, historyPageSig: null,
  correctivePage: 1, correctivePageSig: null,
  schedulePage: 1, schedulePageSig: null,
  auditOpenPage: 1,
  auditStartablePage: 1,
  usersPage: 1,
  correctiveDetailId: null,
  correctiveDetailReturnTo: null,
  correctiveAuditFilter: null,
  correctiveFilterWp: '',
  correctiveFilterType: '',
  correctiveFilterGroup: '',
  correctiveFilterStatus: 'v_reseni',
  correctiveFilterResponsible: '',
  correctiveFilterAuditor: '',
  correctiveOverdueOnly: false,
  correctiveSearch: '',
  correctiveDateFrom: '',
  correctiveDateTo: '',
  correctiveFiltersOpen: false,
  correctivePendingStatus: null,
  correctivePendingNote: '',
  correctivePendingAttachments: [],
  correctiveSortBy: 'termin',
  correctiveSortDir: 'asc',
  scheduleSearch: '',
  scheduleFilterWp: '',
  scheduleFilterType: '',
  scheduleFilterGroup: '',
  scheduleFilterStatus: 'naplanovano',
  scheduleFiltersOpen: false,
  scheduleSortBy: 'termin',
  scheduleSortDir: 'asc',
  historySelectedIds: new Set(),
  historyShowTrash: false,
  confirmTarget: '',
  editWpId: null,
  editTypeId: null,
  questionsEditTypeId: null,
  editGroupId: null,
  editQuestionId: null,
  editSubQuestionId: null,
  newAuditWp: '',
  newAuditBranch: null,
  newAuditType: '',
  newAuditWps: null, // obchůzka: vybraná pracoviště ve formuláři zahájení
  newAuditPeople: [], // zúčastněné osoby ve formuláři zahájení [{id|null, name}]
  meName: '',
  meEmail: '',
  meAvatarUrl: '',
  meColor: '',
  roles: new Map(),
  rolesReady: false,
  myId: null,
  isOwner: false,
  canEditPlatform: false,
  canWriteData: false,
  newUserEmail: '',
  newUserRole: 'user',
  roleProfiles: {},
  roleProfilesKey: '',
  draggingGroupId: null,
  expandedQuestions: new Set(),
  profiles: new Map(),
  dashboardSeeded: false,
  viewAsRole: null,
  branding: {companyName:'', logoAssetId:'', logoUrl:''},
  editCompanyId: null,
  companyDraftFor: undefined,
  companyDraftName: '',
  companyDraftIco: '',
  companyDraftAddress: '',
  companyDraftHeight: 32,
  scoreDefaults: {good:80, bad:50},
  scope: loadScopes()
};
var SCORE_FACTORY_DEFAULT = {good:80, bad:50};
var SCHEDULE_START_WINDOW_DEFAULT = 7;
// Lhůta po termínu (rozhodnutí uživatele 2026-10-31): termín z plánu jde
// zahájit nejvýš tolik dní po termínu, pak je „Neprovedeno“ (appSettings/scheduleLate).
var SCHEDULE_LATE_DEFAULT = 7;
// Rozpracovaný audit se po N hodinách od zahájení automaticky zruší
// (Edge Function expire-open-audits, pg_cron každou hodinu). Výchozí 24 h,
// Admin mění v Pravidlech auditů (appSettings/openAuditTtl {hours}).
var OPEN_AUDIT_TTL_HOURS = 24;
var OPEN_AUDIT_TTL_MAX = 168;
function openAuditTtlHours(){ return (typeof state.openAuditTtlHours==='number' && state.openAuditTtlHours>=1) ? state.openAuditTtlHours : OPEN_AUDIT_TTL_HOURS; }
var NOK_THRESHOLD_DEFAULT = 1; // audit je NOK od 1 NOK otázky (Pravidla auditů → appSettings/nokThreshold)
function defaultNokThreshold(){ return (typeof state.nokThresholdDefault==='number' && state.nokThresholdDefault>=1) ? state.nokThresholdDefault : NOK_THRESHOLD_DEFAULT; }

var ROLE_LABELS = {administrator:'Administrátor', spravce:'Správce', user:'Uživatel'};
var writeChain = Promise.resolve();

function esc(s){
  return String(s==null?'':s).replace(/[&<>"']/g, function(c){
    return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c];
  });
}
function fmtDate(iso){
  if(!iso) return '—';
  var d = new Date(iso);
  if(isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('cs-CZ',{day:'2-digit',month:'2-digit',year:'numeric'}) + ' ' +
    d.toLocaleTimeString('cs-CZ',{hour:'2-digit',minute:'2-digit'});
}
function fmtDateShort(iso){
  if(!iso) return '—';
  var d = new Date(iso);
  if(isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('cs-CZ',{day:'2-digit',month:'2-digit',year:'2-digit'});
}
// Termín naplánovaného auditu (viz scheduleOccurrenceObjects) — datum, a
// pokud má plán vyplněný čas (Správa plánů auditů -> "Čas"), i ten za ním.
function fmtOccurrenceDate(o){
  return fmtDateShort(o.dateStr) + (o.time ? ' ' + o.time : '');
}
function uid(prefix){
  return (prefix||'id') + '_' + Date.now().toString(36) + Math.random().toString(36).slice(2,8);
}
// Strips diacritics (á->a, č->c, ř->r, š->s, ž->z, …) so fulltext search
// matches regardless of accents — "radic" finds "řadič" and vice versa.
function normalizeSearchText(s){
  return (s||'').toString().normalize('NFD').replace(/[̀-ͯ]/g,'').toLowerCase();
}
function nextOrder(map){
  var max = -1;
  map.forEach(function(v){ if(typeof v.order==='number' && v.order>max) max = v.order; });
  return max+1;
}
function showToast(msg){
  var t = document.getElementById('toast');
  t.textContent = msg;
  t.classList.add('show');
  clearTimeout(t._tm);
  t._tm = setTimeout(function(){ t.classList.remove('show'); }, 2600);
}
function queueWrite(fn){
  // The written row refreshes itself right after the write lands (see
  // reloadStoredRow in the Supabase module) instead of waiting on Realtime.
  writeChain = writeChain.then(fn).catch(function(e){
    if(!(e && e.code==='read_only')) console.error('db write failed', e);
    // '42501' is Postgres's "insufficient_privilege" code — what a Supabase
    // RLS policy rejection actually comes back as. This used to check for
    // 'invalid_argument', a leftover from before the Supabase migration
    // (that was the error code the old Claude-platform db capability used
    // for a denied write), which Supabase never returns — so this specific
    // message could never actually show, and every permission failure fell
    // through to the generic one below instead.
    if(e && (e.code==='read_only' || e.code==='P0001') && e.message){
      // read_only = licence (supabase-layer), P0001 = česká hláška z databáze (počet licencí…)
      showToast(e.message);
    } else if(e && e.code==='42501'){
      showToast('Nemáte dostatečná oprávnění pro tuto akci.');
    } else {
      showToast('Uložení se nezdařilo: ' + (e && e.code ? e.code : 'chyba'));
    }
  });
  return writeChain;
}

function sortedAuditTypes(){
  return Array.from(state.auditTypes.entries()).filter(function(e){ return matchesCurrentCompany(e[1]); })
    .sort(function(a,b){ return (a[1].order||0)-(b[1].order||0); });
}
// ---- Skupiny auditů (rozhodnutí uživatele 2026-10-28) ----
// Druh auditu je nejvýš v jedné skupině: audit_types.data.auditGroup
// (název). Skupiny = názvy u druhů aktivní firmy. Filtry berou skupinu
// podle dnešního nastavení druhu (do uložených auditů se nezapisuje).
function auditGroupOfType(typeId){ var t = typeId && state.auditTypes.get(typeId); return (t && t.auditGroup) || ''; }
function sortedAuditGroups(){
  var seen = {};
  sortedAuditTypes().forEach(function(e){ if(e[1].auditGroup) seen[e[1].auditGroup] = true; });
  return Object.keys(seen).sort(function(a,b){ return a.localeCompare(b,'cs'); });
}
function typesInGroup(types, group){ return group ? types.filter(function(e){ return (e[1].auditGroup||'')===group; }) : types; }
function matchesAuditGroup(typeId, group){ return !group || auditGroupOfType(typeId)===group; }
// Pole filtru „Skupina auditů“ (jen když nějaká skupina existuje).
function auditGroupFilterFieldHtml(selId, value){
  var groups = sortedAuditGroups();
  if(!groups.length && !value) return '';
  return '<div class="field"><label>Skupina auditů</label>' +
    '<select class="filter-select" id="'+selId+'"><option value="">Všechny skupiny</option>' +
    groups.map(function(g){ return '<option value="'+esc(g)+'" '+(value===g?'selected':'')+'>'+esc(g)+'</option>'; }).join('') +
    '</select></div>';
}
// Výběr „Skupina auditů“ před výběrem druhu (zahájení auditu, plán) — jen
// když nějaká skupina existuje; hodnota '' = všechny skupiny.
function auditGroupPickHtml(selId, value, onchange){
  var groups = sortedAuditGroups();
  if(!groups.length) return '';
  return '<div class="field"><label>Skupina auditů</label><select id="'+selId+'"'+(onchange?' onchange="'+onchange+'"':'')+'>' +
    '<option value="">Všechny skupiny</option>' +
    groups.map(function(g){ return '<option value="'+esc(g)+'" '+(value===g?'selected':'')+'>'+esc(g)+'</option>'; }).join('') +
    '</select></div>';
}
function sortedGroups(){
  return Array.from(state.groups.entries()).sort(function(a,b){ return (a[1].order||0)-(b[1].order||0); });
}
function groupsForType(typeId){
  return sortedGroups().filter(function(e){ return e[1].auditTypeId===typeId; });
}
function questionsInGroup(gid){
  return Array.from(state.questions.entries())
    .filter(function(e){ return e[1].groupId===gid; })
    .sort(function(a,b){ return (a[1].order||0)-(b[1].order||0); });
}
function questionCountForType(typeId){
  var n = 0;
  groupsForType(typeId).forEach(function(ge){ n += questionsInGroup(ge[0]).length; });
  return n;
}
function subQuestionsInQuestion(qid){
  return Array.from(state.subquestions.entries())
    .filter(function(e){ return e[1].questionId===qid; })
    .sort(function(a,b){ return (a[1].order||0)-(b[1].order||0); });
}
// Which subjekty (companies) the signed-in person may see at all — null
// means unrestricted (administrator). Everyone else is limited to the
// companyIds the admin granted them on their role doc (state.roles),
// assigned from "Uživatelé a role". This mirrors the can_access_company()
// rule enforced server-side in Postgres RLS — the app-level check below
// just keeps the UI from ever offering/rendering what the database would
// refuse anyway, it isn't the actual security boundary.
function myAccessibleCompanyIds(){
  if(realRole()==='administrator') return null;
  var r = state.myId ? state.roles.get(state.myId) : null;
  if(roleAllCompanies(r)) return null;
  return roleCompanyIds(r);
}
// Přístup k firmám na roli: companyIds (vybrané firmy) nebo allCompanies
// (všechny firmy organizace, i později založené — stejně v RLS
// my_company_ids() a v Edge Function calendar-feed).
function roleAllCompanies(r){ return !!(r && r.allCompanies===true); }
function roleCompanyIds(r){ return (r && Array.isArray(r.companyIds)) ? r.companyIds : []; }
function roleHasCompany(r, cid){
  if(!r) return false;
  if(r.role==='administrator' || roleAllCompanies(r)) return true;
  return roleCompanyIds(r).indexOf(cid)!==-1;
}
// Počet existujících firem, které člověk vidí.
function roleAccessCount(r){
  if(r && (r.role==='administrator' || roleAllCompanies(r))) return state.companies.size;
  return roleCompanyIds(r).filter(function(cid){ return state.companies.has(cid); }).length;
}
// Uživatelé a Správci (ne Administrátoři), seřazení podle jména.
function nonAdminRoleIds(){
  return Array.from(state.roles.keys()).filter(function(id){
    var r = state.roles.get(id);
    return r && r.role!=='administrator';
  }).sort(function(a,b){ return accessName(a).localeCompare(accessName(b), 'cs'); });
}
function usersWithoutCompanyAccess(){
  return nonAdminRoleIds().filter(function(id){ return roleAccessCount(state.roles.get(id))===0; });
}
function isCompanyAccessible(companyId){
  var allow = myAccessibleCompanyIds();
  if(allow===null) return true;
  if(!companyId) return true; // company-less/shared content (e.g. default question bank) stays visible to everyone
  return allow.indexOf(companyId)!==-1;
}
// A record "belongs" to the currently selected legal entity if its companyId
// matches, or defensively if it has no companyId yet (e.g. briefly during
// migration) — never hide data because of a missing field. Access to the
// subjekt itself (isCompanyAccessible) is checked first and separately.
function matchesCurrentCompany(obj){
  if(!isCompanyAccessible(obj.companyId)) return false;
  return !state.currentCompanyId || !obj.companyId || obj.companyId===state.currentCompanyId;
}
function sortedCompanies(){
  return Array.from(state.companies.entries())
    .filter(function(e){ return isCompanyAccessible(e[0]); })
    .sort(function(a,b){
      return (a[1].order||0)-(b[1].order||0) || (a[1].name||'').localeCompare(b[1].name||'', 'cs');
    });
}
function currentCompany(){
  return state.companies.get(state.currentCompanyId) || null;
}
function workplaceCountForCompany(companyId){
  var n = 0;
  state.workplaces.forEach(function(w){ if(w.companyId===companyId) n++; });
  return n;
}
function companyAuditIds(companyId){
  var ids = [];
  state.audits.forEach(function(a, id){ if(a.companyId===companyId) ids.push(id); });
  return ids;
}
function correctiveActionKeysForAudits(auditIds){
  var set = new Set(auditIds);
  var keys = [];
  state.correctiveActions.forEach(function(v, key){ if(set.has(key.split('::')[0])) keys.push(key); });
  return keys;
}
function auditTypeIdsForCompany(companyId){
  var ids = [];
  state.auditTypes.forEach(function(t, tid){ if(t.companyId===companyId) ids.push(tid); });
  return ids;
}
function auditIdsForWorkplace(workplaceId){
  var ids = [];
  state.audits.forEach(function(a, id){ if(a.workplaceId===workplaceId) ids.push(id); });
  return ids;
}
// Pracoviště, kterých se audit týká: jeho pracoviště + pracoviště volných zjištění.
// Filtr pracoviště s podřízenými (2026-11-03): hodnota = nadřazené (včetně
// podřízených, „— vše —“) nebo konkrétní podřízené.
function wpFilterIds(v){
  if(!v) return null;
  var w = state.workplaces.get(v);
  return (w && w.parentId && state.workplaces.has(w.parentId)) ? [v] : workplaceWithChildrenIds(v);
}
function wpFilterMatchesId(v, wpId){
  var ids = wpFilterIds(v);
  return !ids || ids.indexOf(wpId)>=0;
}
function wpFilterMatchesAny(v, wpIds){
  var ids = wpFilterIds(v);
  return !ids || (wpIds||[]).some(function(id){ return ids.indexOf(id)>=0; });
}
function auditMatchesWpFilter(a, v){
  var ids = wpFilterIds(v);
  return !ids || ids.some(function(id){ return auditTouchesWorkplace(a, id); });
}
// Pole filtru: Pracoviště (první úroveň, id selectId — dosavadní obsluha beze
// změny) + Podřízené pracoviště („— vše —“), když vybrané nějaká má.
// setter = JS výraz s %s pro hodnotu, např. "App.setHistoryWpFilter(%s)".
function wpFilterFieldsHtml(selectId, value, wps, setter){
  var sel = value ? state.workplaces.get(value) : null;
  var topId = sel && sel.parentId && state.workplaces.has(sel.parentId) ? sel.parentId : (value||'');
  var inList = {}; wps.forEach(function(e){ inList[e[0]] = true; });
  var tops = wps.filter(function(e){ return !e[1].parentId || !inList[e[1].parentId]; });
  var kids = topId ? wps.filter(function(e){ return e[1].parentId===topId; }) : [];
  var html = '<div class="field"><label>Pracoviště</label>' +
    '<select class="filter-select" id="'+selectId+'"><option value="">Všechna pracoviště</option>' +
    tops.map(function(e){ return '<option value="'+e[0]+'" '+(topId===e[0]?'selected':'')+'>'+esc(e[1].name)+'</option>'; }).join('') +
    '</select></div>';
  if(kids.length){
    html += '<div class="field"><label>Podřízené pracoviště</label>' +
      '<select class="filter-select" id="'+selectId+'Sub" onchange="'+setter.replace('%s', "this.value||'"+topId+"'")+'"><option value="">— vše —</option>' +
      kids.map(function(e){ return '<option value="'+e[0]+'" '+(value===e[0]?'selected':'')+'>'+esc(e[1].name)+'</option>'; }).join('') +
      '</select></div>';
  }
  return html;
}
function auditTouchesWorkplace(a, wpId){
  if(!a || !wpId) return false;
  if(auditWorkplaceIds(a).indexOf(wpId)>=0) return true;
  return Array.isArray(a.findings) && a.findings.some(function(f){ return f.workplaceId===wpId; });
}
// Every Storage attachment id "owned" by the given audits — their own
// answer/subquestion photos plus any photos attached to a status-change
// entry in the matching correctiveActions history. This is the single
// place that knows where a photo can be hiding, so every cascade delete
// (single audit, workplace, branch, company) can reuse it instead of
// re-deriving the list (and risking missing a spot) each time.
function collectAttachmentIdsForAudits(auditIds){
  var set = new Set(auditIds);
  var ids = [];
  state.audits.forEach(function(a, id){
    if(!set.has(id)) return;
    (a.answers||[]).forEach(function(ans){
      (ans.attachments||[]).forEach(function(att){ if(att && att.id) ids.push(att.id); });
      (ans.subAnswers||[]).forEach(function(sa){
        (sa.attachments||[]).forEach(function(att){ if(att && att.id) ids.push(att.id); });
      });
    });
    (a.findings||[]).forEach(function(f){
      (f.attachments||[]).forEach(function(att){ if(att && att.id) ids.push(att.id); });
    });
  });
  correctiveActionKeysForAudits(auditIds).forEach(function(key){
    var rec = state.correctiveActions.get(key);
    if(!rec) return;
    (rec.history||[]).forEach(function(entry){
      (entry.attachments||[]).forEach(function(att){ if(att && att.id) ids.push(att.id); });
    });
  });
  return ids;
}
// The one place audits (and everything that hangs off them — their
// corrective-action records and every photo either one owns) get
// permanently removed. Used by the trash purge (manual and automatic
// after 90 days) and by deleting a workplace/branch/company, so none of
// those paths can forget to clean up Storage again in the future.
// `precollected`, when given, is {caKeys, attachmentIds} gathered by the
// caller BEFORE it touched local state. Some callers (e.g.
// App.purgeAuditPermanently) optimistically remove the audit from
// state.audits synchronously, for an instant UI update, and only then
// queue this cascade — by the time it would run, the answers/attachments
// that collectAttachmentIdsForAudits() needs are already gone from local
// state. Precomputing keeps those callers correct without forcing every
// caller to care about the ordering.
function cascadeDeleteAudits(auditIds, precollected){
  if(!db || !auditIds || !auditIds.length) return Promise.resolve();
  var caKeys = (precollected && precollected.caKeys) || correctiveActionKeysForAudits(auditIds);
  var attachmentIds = (precollected && precollected.attachmentIds) || collectAttachmentIdsForAudits(auditIds);
  var storageCleanup = (assetsCap && attachmentIds.length) ? assetsCap.deleteMany(attachmentIds).catch(function(){}) : Promise.resolve();
  var dbDeletes = Promise.all(
    auditIds.map(function(id){ return db.doc('audits/'+id).delete(); })
      .concat(caKeys.map(function(key){ return db.doc('correctiveActions/'+key).delete(); }))
  );
  return Promise.all([storageCleanup, dbDeletes]);
}
// Podřízená pracoviště (rozhodnutí uživatele 2026-11-03): workplaces.parentId z portálu,
// jen jedna úroveň. Řazení jako strom (nadřazené, hned pod ním jeho podřízená),
// název podřízeného „Hala A › Linka 1“ (workplaceLabel).
function workplaceParent(w){
  var pid = w && w.parentId;
  return pid ? (state.workplaces.get(pid) || null) : null;
}
function workplaceLabel(w){
  if(typeof w === 'string') w = state.workplaces.get(w);
  if(!w) return '';
  var p = workplaceParent(w);
  return p ? (p.name||'')+' › '+(w.name||'') : (w.name||'');
}
function workplaceChildren(id){
  return sortedWorkplaces().filter(function(e){ return e[1].parentId===id; });
}
// id pracoviště + jeho podřízených (pro filtry a stránku nadřazeného)
function workplaceWithChildrenIds(id){
  return [id].concat(workplaceChildren(id).map(function(e){ return e[0]; }));
}
function sortedWorkplaces(){
  var byName = function(a,b){ return (a.name||'').localeCompare(b.name||'', 'cs'); };
  return Array.from(state.workplaces.entries()).filter(function(e){ return matchesCurrentCompany(e[1]); })
    .sort(function(a,b){
      var pa = workplaceParent(a[1]) || a[1], pb = workplaceParent(b[1]) || b[1];
      var c = byName(pa, pb);
      if(c) return c;
      var ka = a[1].parentId || a[0], kb = b[1].parentId || b[0];
      if(ka!==kb) return ka < kb ? -1 : 1;
      var ca = a[1]===pa ? 0 : 1, cb = b[1]===pb ? 0 : 1;
      return ca - cb || byName(a[1], b[1]);
    });
}
function sortedBranches(){
  return Array.from(state.branches.entries()).filter(function(e){ return matchesCurrentCompany(e[1]); })
    .sort(function(a,b){ return (a[1].order||0)-(b[1].order||0); });
}
function branchLabel(branchId){
  if(!branchId) return 'Sídlo firmy';
  var b = state.branches.get(branchId);
  return b ? b.name : 'Sídlo firmy';
}
function groupWorkplacesByBranch(){
  var groups = new Map(); // branchId ('' = sídlo) -> array of [id, workplace]
  sortedWorkplaces().forEach(function(e){
    var bId = e[1].branchId || '';
    if(!groups.has(bId)) groups.set(bId, []);
    groups.get(bId).push(e);
  });
  var order = [''].concat(sortedBranches().map(function(e){ return e[0]; }));
  return order.filter(function(bId){ return groups.has(bId); }).map(function(bId){
    return {branchId:bId, label:branchLabel(bId), items:groups.get(bId)};
  });
}
function realRole(){
  if(state.isOwner) return 'administrator';
  var r = state.myId ? state.roles.get(state.myId) : null;
  if(r && r.role==='administrator') return 'administrator';
  if(r && r.role==='spravce') return 'spravce';
  if(r && r.role==='user') return 'user';
  if(state.canEditPlatform) return 'spravce';
  // Role "Pouze čtení" byla zrušena — kdo nemá roli, je Uživatel.
  return 'user';
}
function myRole(){
  var real = realRole();
  // Administrators can preview the app as another role (UI-only; their
  // real role in the `roles` table, and what Supabase's RLS policies
  // enforce, is unchanged).
  if(real==='administrator' && state.viewAsRole && state.viewAsRole!==real){
    return state.viewAsRole;
  }
  return real;
}
function canManageContent(){ var r = myRole(); return r==='administrator' || r==='spravce'; }
function canManageUsers(){ return myRole()==='administrator'; }
function canWriteAudits(){ return true; } // každá role smí provádět audity
// "Moje pracoviště" = pracoviště, kde jsem odpovědná osoba (responsibleUserId;
// stejná definice jako Moje úkoly, e-mailová upozornění a RLS my_workplace_ids).
function myWorkplaceIdSet(){
  var set = {};
  if(!state.myId) return set;
  var get = function(id){ return state.workplaces.get(id); };
  state.workplaces.forEach(function(w, wid){ if(workplaceResponsibleId(w, get)===state.myId) set[wid]=true; });
  return set;
}
function isMyWorkplace(wid){ return !!(wid && myWorkplaceIdSet()[wid]); }
// Opatření je "moje", když jsem u něj odpovědná osoba, nebo je na mém pracovišti.
function isMyCorrective(item){ return isCorrectiveAssignedToMe(item) || isMyWorkplace(item.workplaceId); }
function isMyOpenAudit(a){ return !!(a && a.auditorId && a.auditorId===state.myId); }
// Odpovědnou osobu a termín opatření mění Admin/Správce, nebo Uživatel
// odpovědný za pracoviště, na kterém opatření je.
function canEditCorrectiveFields(item){
  var r = myRole();
  if(r==='administrator' || r==='spravce') return true;
  return !!item && isMyWorkplace(item.workplaceId);
}
// Rozsah stránky: 'mine' | 'workplaces' | 'all'. „Moje pracoviště“ bez
// přidělených pracovišť se chová jako „Moje“ (volba je pak vypnutá).
function scopeOf(pg){
  var m = (state.scope && state.scope[pg]) || 'mine';
  if(m==='workplaces' && !hasMyWorkplaces()) return 'mine';
  return m;
}
function scopeMine(pg){ return scopeOf(pg)==='mine'; }
// Množina mých pracovišť, když stránka ukazuje „Moje pracoviště“, jinak null.
function scopeWorkplaceSet(pg){ return scopeOf(pg)==='workplaces' ? cachedValue('myWorkplaceIdSet', myWorkplaceIdSet) : null; }
function hasMyWorkplaces(){ return Object.keys(cachedValue('myWorkplaceIdSet', myWorkplaceIdSet)).length>0; }
// Uzavřený audit je „můj“ (Historie i Přehled s „Moje“), když jsem ho
// prováděl — rozhodnutí uživatele 2026-10-28 (dřív i audity mého pracoviště).
// Moje audity = prováděl jsem je, nebo jsem v nich vybraný jako zúčastněná
// osoba (spoluauditor, participantIds — rozhodnutí uživatele 2026-10-29).
function isMyClosedAudit(a){ return !!(a && ((a.auditorId && a.auditorId===state.myId) || isCoAuditor(a))); }
// Docházka (rozhodnutí uživatele 2026-10-31): participantAbsent = účty
// označené v auditu jako nepřítomné — ty se za spoluauditory nepočítají.
function isCoAuditor(a){ return !!(a && state.myId && auditPresentParticipantIds(a).indexOf(state.myId)>=0); }
function auditPresentParticipantIds(a){ return presentParticipantIds(a); }
// „Petr Novák (zástup za Janu Malou)“ — audit provedený zástupcem auditora termínu.
function auditAuditorLabel(a){
  if(!a) return '';
  return (a.auditorName||'—') + (a.delegatedFromName ? ' (zástup za: '+a.delegatedFromName+')' : '');
}
function profileDisplayName(id){ var p = state.profiles.get(id); return (p && (p.displayName || p.email)) || ''; }
// Jména nepřítomných / přítomných zúčastněných (detail, PDF, CSV).
function auditAbsentNames(a){
  return ((a && Array.isArray(a.participantAbsent)) ? a.participantAbsent : []).filter(function(id){ return (a.participantIds||[]).indexOf(id)>=0; }).map(profileDisplayName).filter(Boolean);
}
function auditPresentNames(a){
  var absent = auditAbsentNames(a).slice();
  return ((a && a.participants) || []).filter(function(n){ var i = absent.indexOf(n); if(i>=0){ absent.splice(i,1); return false; } return true; });
}
// Přepínač Moje / Moje pracoviště / Vše — první položka v panelu Filtry;
// na tlačítku Filtry je štítek s aktuální volbou, aby byl vidět i při
// zavřeném panelu.
function scopeToggleHtml(pg){
  var cur = scopeOf(pg), hasWp = hasMyWorkplaces();
  function btn(mode, label, disabled){
    return '<button type="button" class="'+(cur===mode?'on':'')+'"'+(disabled?' disabled title="Nemáte přidělené žádné pracoviště"':'')+
      ' onclick="App.setScope(\''+pg+'\',\''+mode+'\')">'+label+'</button>';
  }
  return '<div class="field" style="margin-bottom:10px"><label>Zobrazit</label>' +
    '<div class="pill-select scope-toggle" role="group" aria-label="Rozsah zobrazení" style="margin-bottom:0">' +
    btn('mine','Moje') + btn('workplaces','Moje pracoviště', !hasWp) + btn('all','Vše') + '</div>' +
    (hasWp ? '' : '<div class="set-note" style="margin-top:6px">Nemáte přidělené žádné pracoviště.</div>') + '</div>';
}
// Konec každého panelu Filtrů (rozhodnutí uživatele 2026-11-03): Vymazat filtry
// (vše zrušit, i Moje → Vše) a Výchozí nastavení (tovární výchozí stránky) —
// to se skryje, když filtr výchozí už je (pg = stránka s Moje/Vše, výchozí Moje;
// restDefault = ostatní pole ve výchozím stavu).
function filterActionsHtml(fn, pg, restDefault){
  var isDefault = !!restDefault && (!pg || scopeOf(pg)==='mine');
  return '<div class="filter-actions">' +
    '<button type="button" class="btn btn-sm btn-ghost" onclick="App.'+fn+'(false)">Vymazat filtry</button>' +
    (isDefault ? '' : '<button type="button" class="btn btn-sm btn-ghost" onclick="App.'+fn+'(true)">Výchozí nastavení</button>') + '</div>';
}
function scopeBadgeHtml(pg){
  var m = scopeOf(pg);
  return '<span class="scope-badge">'+(m==='all'?'Vše':m==='workplaces'?'Pracoviště':'Moje')+'</span>';
}
// Jednoduché pořadové číslo auditu ("Audit č. 24") — číslováno zvlášť
// za každou firmu (subjekt), od 1. Počítá se jako nejvyšší dosud použité
// číslo v rámci dané firmy + 1 (z lokálně načteného state.audits), ne
// přes transakci — appka s tímhle datovým úložištěm transakce nemá, takže
// při vzácné současné kolizi (dva lidé založí audit ve stejný okamžik za
// stejnou firmu) může výjimečně dojít k duplicitě čísla. Přijatelné riziko,
// podobné papírové číselné řadě.
// Audity s kódem druhu (auditCode, např. „BOZP“ nebo „S-BOZP“) mají
// vlastní řadu za firmu a kód; starší audity bez kódu svou řadu za firmu.
function nextAuditNumberForCompany(companyId, auditCode){
  var max = 0;
  state.audits.forEach(function(a){
    if((a.companyId||'')===(companyId||'') && (a.auditCode||'')===(auditCode||'') && a.auditNumber>max) max = a.auditNumber;
  });
  return max+1;
}
// Označení auditu: „BOZP-12“ (s kódem druhu), u starších auditů „#12“.
function auditNoLabel(a){
  if(!a || a.auditNumber==null) return '';
  return a.auditCode ? a.auditCode+'-'+a.auditNumber : '#'+a.auditNumber;
}
// Totéž bez „#“ (údaj Číslo auditu, CSV, tabulka).
function auditNoValue(a){
  if(!a || a.auditNumber==null) return '';
  return a.auditCode ? a.auditCode+'-'+a.auditNumber : String(a.auditNumber);
}
// Řadicí klíč čísla auditu (kód, pak číslo).
function auditNoSortKey(a){
  if(!a || a.auditNumber==null) return null;
  return (a.auditCode||'') + '-' + ('0000000'+a.auditNumber).slice(-8);
}
// Zbývající čas rozpracovaného auditu (ms) do automatického zrušení.
function openAuditRemainingMs(a){
  var t = new Date((a && a.startedAt) || 0).getTime();
  return t ? t + openAuditTtlHours()*3600000 - Date.now() : Infinity;
}
function openAuditRemainingText(a){
  var ms = openAuditRemainingMs(a);
  if(!isFinite(ms)) return '';
  if(ms<=0) return 'čas vypršel';
  var h = Math.floor(ms/3600000), m = Math.floor((ms%3600000)/60000);
  return 'zbývá ' + (h ? h+' h ' : '') + m + ' min';
}
// Rozpracované audity — po vypršení lhůty se už nenabízí (server je smaže).
function openAudits(){
  return Array.from(state.audits.entries()).filter(function(e){ return e[1].status==='open' && matchesCurrentCompany(e[1]) && openAuditRemainingMs(e[1])>0; })
    .sort(function(a,b){ return new Date(b[1].startedAt)-new Date(a[1].startedAt); });
}
// Mezipaměť odvozených seznamů (uzavřené audity, opatření, termíny plánů)
// platná JEN během jednoho render() — navigace, Moje úkoly i obsah je
// počítají opakovaně. Mimo render() (renderCache = null) se vždy počítá
// znovu, takže nikdy nemůže vrátit zastaralý stav. Pole se vrací jako
// kopie (volající je mohou řadit/filtrovat), položky se nemění.
var renderCache = null;
// Platnost mezipaměti = jedno překreslení (render()): začít / vrátit předchozí.
function beginRenderCache(){ var outer = renderCache; renderCache = {}; return outer; }
function endRenderCache(outer){ renderCache = outer; }
function cachedList(key, compute){
  if(!renderCache) return compute();
  if(!renderCache.hasOwnProperty(key)) renderCache[key] = compute();
  return renderCache[key].slice();
}
function cachedValue(key, compute){
  if(!renderCache) return compute();
  if(!renderCache.hasOwnProperty(key)) renderCache[key] = compute();
  return renderCache[key];
}
function closedAudits(){
  return cachedList('closedAudits', function(){
    return Array.from(state.audits.entries()).filter(function(e){ return e[1].status==='closed' && !e[1].deletedAt && matchesCurrentCompany(e[1]); })
      .sort(function(a,b){ return new Date(b[1].closedAt||0)-new Date(a[1].closedAt||0); });
  });
}
// Audits soft-deleted (App.deleteAudit sets deletedAt instead of removing the
// doc outright) — shown in the Historie "Koš" so a mistaken delete can be
// undone within DELETED_RETENTION_DAYS, after which maybePurgeOldDeletedAudits
// removes them for good.
var DELETED_RETENTION_DAYS = 30;

export {
  openAuditRemainingMs,
  openAuditRemainingText,
  SCHEDULE_LATE_DEFAULT,
  OPEN_AUDIT_TTL_HOURS,
  OPEN_AUDIT_TTL_MAX,
  openAuditTtlHours,
  auditWorkplaceIds,
  auditTouchesWorkplace,
  workplacesLabel,
  DASHBOARD_KEY,
  DELETED_RETENTION_DAYS,
  ICONS,
  NOK_THRESHOLD_DEFAULT,
  ROLE_LABELS,
  SCHEDULE_START_WINDOW_DEFAULT,
  SCOPE_MODES,
  SCOPE_PAGES,
  SCORE_FACTORY_DEFAULT,
  auditGroupFilterFieldHtml,
  auditGroupPickHtml,
  auditGroupOfType,
  auditIdsForWorkplace,
  auditNoLabel,
  auditNoSortKey,
  auditNoValue,
  auditTypeIdsForCompany,
  beginRenderCache,
  branchLabel,
  cachedList,
  cachedValue,
  canEditCorrectiveFields,
  canManageContent,
  canManageUsers,
  canWriteAudits,
  cascadeDeleteAudits,
  closedAudits,
  collectAttachmentIdsForAudits,
  companyAuditIds,
  correctiveActionKeysForAudits,
  currentCompany,
  dashboardConfig,
  defaultNokThreshold,
  endRenderCache,
  esc,
  fmtDate,
  fmtDateShort,
  fmtOccurrenceDate,
  groupWorkplacesByBranch,
  groupsForType,
  hasMyWorkplaces,
  isCompanyAccessible,
  isMyClosedAudit,
  isCoAuditor,
  auditPresentParticipantIds,
  auditAbsentNames,
  auditPresentNames,
  profileDisplayName,
  auditAuditorLabel,
  isMyCorrective,
  myWorkplaceIdSet,
  isMyOpenAudit,
  matchesAuditGroup,
  matchesCurrentCompany,
  maybeProcessDeepLink,
  myRole,
  nextAuditNumberForCompany,
  nextOrder,
  nonAdminRoleIds,
  normalizeSearchText,
  openAudits,
  parseDashboardConfig,
  questionCountForType,
  questionsInGroup,
  queueWrite,
  realRole,
  roleAccessCount,
  roleAllCompanies,
  roleCompanyIds,
  roleHasCompany,
  saveCurrentCompanyId,
  saveDashboardConfig,
  saveScope,
  scopeBadgeHtml,
  scopeMine,
  scopeToggleHtml,
  filterActionsHtml,
  scopeWorkplaceSet,
  setDashboardConfig,
  showToast,
  sortedAuditGroups,
  sortedAuditTypes,
  sortedBranches,
  sortedCompanies,
  sortedWorkplaces,
  wpFilterIds,
  wpFilterMatchesId,
  wpFilterMatchesAny,
  auditMatchesWpFilter,
  wpFilterFieldsHtml,
  workplaceChildren,
  workplaceLabel,
  workplaceParent,
  workplaceWithChildrenIds,
  state,
  subQuestionsInQuestion,
  typesInGroup,
  uid,
  usersWithoutCompanyAccess,
  workplaceCountForCompany
};
